import { Box, Flex } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet-async"
import { Link, useParams } from "react-router-dom"

import {
  CategoryItemsContainer,
  Content,
  LinkArrow,
  ReadmoreLink,
  SearchBox,
  SubTitle,
  TitleBorder,
} from "areas/helpdesk/index"
import Loading from "areas/helpdesk/loading"
import { trimToLastSentence, jewlr_t } from "helpers/application"
import fetchCollectionData from "hoc/helpdesk/fetch-collection-results"
import withHelpdesk from "hoc/helpdesk/with-helpdesk"

const Category = ({ results }) => {
  const { collection_id: collectionId, category_id: categoryId } = useParams()
  const collection = results[collectionId]

  if (!collection) {
    return <Loading />
  }

  const category = collection.categories[categoryId]

  if (!category) {
    return <Loading />
  }

  if (category.articles == false) {
    return <Loading />
  } else if (Object.keys(category.articles).length == 0) {
    return "No articles found"
  }

  return (
    <Box>
      <Helmet>
        {/* eslint-disable sort-keys */}
        <title>
          {jewlr_t("terms.Jewelry")} FAQs - {category.name}
        </title>
        {Object.keys(category.articles).length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: Object.values(category.articles).map((article) => ({
                "@type": "Question",
                name: article.name,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: trimToLastSentence(article.preview),
                },
              })),
            })}
          </script>
        )}
        {/* eslint-enable sort-keys */}
      </Helmet>
      <TitleBorder data-cy={`category-${category.name}`} mb={4}>
        {category.name}
      </TitleBorder>

      <Flex flexDirection={{ _: "column", tablet: "inherit" }}>
        <Box flex={{ _: "1", tablet: "3" }} my={{ _: 4, tablet: 5 }}>
          <Box mb={{ _: 5, tablet: 0 }} overflow="hidden">
            <CategoryItemsContainer
              addYPadding
              data-cy="helpdesk-category-items"
            >
              {Object.keys(category.articles).map((artId) => {
                const article = category.articles[artId]
                return (
                  <Box
                    display="flex"
                    flex="0 0 50%"
                    flexWrap="wrap"
                    key={`cat-article-${artId}`}
                    minHeight={{ _: "160px", tablet: "300px" }}
                  >
                    <SearchBox data-cy="helpdesk-category-item">
                      <Box height="90%">
                        <Link
                          data-cy="helpdesk-category-item-title"
                          data-lc="category"
                          to={{
                            pathname: `/helpdesk/a/${article.number}/${article.slug}`,
                            state: {
                              category: { id: categoryId, ...category },
                            },
                          }}
                        >
                          <SubTitle as="h3">{article.name}</SubTitle>
                        </Link>
                        <Content>
                          {article.preview}
                          {article.preview.length > 160 ? "..." : ""}
                        </Content>
                      </Box>
                      <ReadmoreLink
                        as={Link}
                        data-lc="category"
                        to={{
                          pathname: `/helpdesk/a/${article.number}/${article.slug}`,
                          state: {
                            category: { id: categoryId, ...category },
                          },
                        }}
                      >
                        <Flex>
                          <LinkArrow /> Keep Reading
                        </Flex>
                      </ReadmoreLink>
                    </SearchBox>
                  </Box>
                )
              })}
            </CategoryItemsContainer>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

Category.propTypes = {
  results: PropTypes.object,
}

export default fetchCollectionData(withHelpdesk(Category))
